import { useId } from 'react';
import { Icon } from '../theme/Icon';
import s from './DataTable.module.css';
import l from './LocalSearchField.module.css';
import { t } from '@app/i18n';

export interface LocalSearchFieldProps {
  value: string;
  onChange: (valor: string) => void;
  /** Placeholder e rótulo acessível do campo. */
  placeholder: string;
  /**
   * Nota ao lado do campo. Padrão: "filtra só a página carregada" — a indicação
   * de FR-096: quem busca precisa saber que o filtro **não foi à fonte**, senão lê
   * "nada encontrado" como "não existe".
   */
  helperText?: string;
  /** Texto do `sr-only` que acompanha o placeholder quando ele é curto. */
  'aria-describedby'?: string;
}

/**
 * Campo de busca **local** (T372): filtra o que já está na tela, nunca a fonte.
 *
 * Existe separado da busca do `DataTable` porque a semântica é outra e precisa
 * ser dita: a busca da tabela paginada pelo servidor vai à fonte; esta olha só
 * as páginas carregadas por cursor (T16) ou o que já foi lido (T23). O mesmo
 * desenho do campo da tabela (`fltin`), com a nota ao lado.
 */
export function LocalSearchField({
  value,
  onChange,
  placeholder,
  helperText = t('common.ui.localSearch.help'),
}: LocalSearchFieldProps) {
  const id = useId();
  const noteID = `${id}-nota`;
  return (
    <span className={l.campo}>
      <span className={s.fltin}>
        <Icon name="search" size="sm" aria-hidden="true" />
        <label className="sr-only" htmlFor={id}>
          {placeholder}
        </label>
        <input
          id={id}
          type="search"
          value={value}
          placeholder={placeholder}
          aria-describedby={noteID}
          onChange={(e) => onChange(e.target.value)}
        />
      </span>
      <span id={noteID} className={l.nota}>
        {helperText}
      </span>
    </span>
  );
}
